import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Download, CalendarRange, X,
  ChevronDown, ChevronRight as ChevRight,
} from "lucide-react";
import { PageShell } from "@/components/SidebarLayout";
import HorizonSelector from "@/components/HorizonSelector";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { TEST_IDS } from "@/testIds";

// ---------------- Collections Model view (Summary + expandable projects) ---------------- //
function typeDot(t) {
  return t === "new" ? "bg-gold-500" : t === "ongoing" ? "bg-ink" : t === "land" ? "bg-[#7BB3F0]" : "bg-muted-light";
}

function ModelShortMonth(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  } catch { return iso; }
}

/** Shared category × month table body used by both the top-summary and
 *  every expanded project block. All numbers in ₹L. Anchored projects pass
 *  their own simplified `categories` (no milestone rows, plus Outstanding).
 */
function CategoryTable({ months, categories, matrix, rowTotals, compact = false, singleRowKey = null }) {
  const rows = singleRowKey
    ? categories.filter((c) => c.key === singleRowKey)
    : categories;

  return (
    <div className="overflow-auto">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-10">
          <tr>
            <th
              className={`sticky left-0 z-20 text-[10px] uppercase tracking-widest font-semibold text-left ${compact ? "px-3 py-2" : "px-4 py-3"}`}
              style={{
                minWidth: 180,
                width: 180,
                background: "var(--cf-surface-2)",
                color: "var(--cf-muted-1)",
                borderBottom: "1px solid var(--cf-hairline)",
              }}
            >
              Category
            </th>
            {months.map((m) => (
              <th
                key={m}
                className={`text-[10px] uppercase tracking-widest font-semibold text-right ${compact ? "px-2 py-2" : "px-2 py-3"}`}
                style={{
                  minWidth: 96,
                  background: "var(--cf-surface-2)",
                  color: "var(--cf-muted-1)",
                  borderBottom: "1px solid var(--cf-hairline)",
                }}
              >
                {ModelShortMonth(m)}
              </th>
            ))}
            <th
              className={`md:sticky md:right-0 z-20 text-[10px] uppercase tracking-widest font-semibold text-right ${compact ? "px-3 py-2" : "px-3 py-3"}`}
              style={{
                background: "var(--cf-surface-2)",
                color: "var(--cf-muted-1)",
                minWidth: 140,
                borderLeft: "2px solid var(--cf-hairline)",
                borderBottom: "1px solid var(--cf-hairline)",
                boxShadow: "-6px 0 12px -6px rgba(18,49,89,0.10)",
              }}
            >
              Row Total
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c, i) => {
            const isTotal = c.key === "total_collection";
            const isCumBase = c.key === "cum_sales_base";
            const isOutstanding = c.key === "outstanding";
            return (
              <tr
                key={c.key}
                data-testid={`collections-row-${c.key}`}
                className="border-b"
                style={{ borderColor: "var(--cf-hairline)" }}
              >
                <td
                  className={`sticky left-0 z-10 ${compact ? "px-3 py-1.5" : "px-4 py-2"}`}
                  style={{
                    minWidth: 180,
                    width: 180,
                    background: isTotal
                      ? "var(--cf-accent-solid)"
                      : (i % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)"),
                    borderRight: "1px solid var(--cf-hairline)",
                  }}
                >
                  <span className={`text-sm ${isTotal ? "font-bold text-ink" : "text-ink"}`}>
                    {c.label}
                  </span>
                  {isCumBase && (
                    <span className="ml-1 text-[9px] uppercase tracking-widest text-muted-light">running</span>
                  )}
                  {isOutstanding && (
                    <span className="ml-1 text-[9px] uppercase tracking-widest text-gold-600">settings</span>
                  )}
                </td>
                {months.map((m) => {
                  const v = matrix?.[c.key]?.[m] ?? 0;
                  return (
                    <td
                      key={m}
                      className={`text-right ${compact ? "px-2 py-1.5" : "px-2 py-2"} num text-xs ${isTotal ? "font-bold text-ink" : "text-ink"} border-l border-hairline/30`}
                      style={{ minWidth: 96 }}
                    >
                      {v === 0 ? <span className="text-muted-light/50">—</span> : formatMoney(v, { showZero: true })}
                    </td>
                  );
                })}
                <td
                  className={`md:sticky md:right-0 z-10 text-right ${compact ? "px-3 py-1.5" : "px-3 py-2"} num text-xs ${isTotal ? "font-bold text-ink" : ""}`}
                  style={{
                    minWidth: 140,
                    /* Must be a fully opaque background — a glassy/translucent
                       fill lets the underlying last-month cell bleed through
                       and reads as visual overlap on horizontal scroll. */
                    background: isTotal
                      ? "var(--cf-accent-solid)"
                      : (i % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)"),
                    borderLeft: "2px solid var(--cf-hairline)",
                    boxShadow: "-6px 0 12px -6px rgba(18,49,89,0.10)",
                  }}
                >
                  {formatMoney(rowTotals?.[c.key] ?? 0, { showZero: true })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SummaryTable({ data }) {
  if (!data) return null;
  return (
    <div className="cf-card overflow-hidden">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-hairline">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Collections Model</div>
          <div className="text-sm text-ink font-semibold">All-projects summary · {data.months.length}m</div>
        </div>
        <div className="text-[10px] text-muted-light">Values in ₹ Lakhs</div>
      </div>
      <div style={{ maxHeight: "58vh" }} className="overflow-auto">
        <CategoryTable
          months={data.months}
          categories={data.summary_categories || data.categories}
          matrix={data.summary.matrix}
          rowTotals={data.summary.row_totals}
        />
      </div>
    </div>
  );
}

function ProjectBreakdown({ data }) {
  const [openIds, setOpenIds] = useState({});
  const toggle = (pid) => setOpenIds((prev) => ({ ...prev, [pid]: !prev[pid] }));
  const expandAll = () => setOpenIds(Object.fromEntries(data.projects.map((p) => [p.project_id, true])));
  const collapseAll = () => setOpenIds({});
  const openCount = Object.values(openIds).filter(Boolean).length;

  if (!data) return null;

  return (
    <div className="cf-card overflow-hidden">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-hairline">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Project-wise Breakdown</div>
          <div className="text-sm text-ink font-semibold">
            {data.projects.length} projects · collapsed shows Sales Booked only
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-light num">{openCount}/{data.projects.length} open</span>
          <button
            data-testid="collections-model-expand-all"
            onClick={expandAll}
            className="cf-touch text-[11px] font-semibold px-2.5 py-1 rounded-full bg-paper text-muted-dark hover:text-ink transition-colors"
          >
            Expand all
          </button>
          <button
            data-testid="collections-model-collapse-all"
            onClick={collapseAll}
            className="cf-touch text-[11px] font-semibold px-2.5 py-1 rounded-full bg-paper text-muted-dark hover:text-ink transition-colors"
          >
            Collapse all
          </button>
        </div>
      </div>

      <div className="divide-y divide-hairline/60">
        {data.projects.map((p) => {
          const open = !!openIds[p.project_id];
          const projTotal = p.row_totals?.total_collection ?? 0;
          return (
            <div key={p.project_id} data-testid={`collections-model-project-${p.project_name}`}>
              <button
                data-testid={`collections-model-toggle-${p.project_name}`}
                onClick={() => toggle(p.project_id)}
                className="cf-touch w-full flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-surface-2/60 transition-colors text-left"
                aria-expanded={open}
              >
                {open
                  ? <ChevronDown size={16} className="text-muted-dark shrink-0" />
                  : <ChevRight size={16} className="text-muted-dark shrink-0" />
                }
                <span className={`inline-block w-2 h-2 rounded-full ${typeDot(p.project_type)}`} />
                <span className="text-sm text-ink font-semibold flex-1 truncate">{p.project_name}</span>
                {p.anchored && (
                  <span className="text-[9px] uppercase tracking-widest text-gold-600 hidden sm:inline">anchored</span>
                )}
                <span className="text-[9px] uppercase tracking-widest text-muted-light hidden sm:inline">{p.project_type}</span>
                <span className="hidden md:inline text-[10px] uppercase tracking-widest text-muted-light">Total {data.months.length}m</span>
                <span className="num text-sm text-ink font-bold min-w-[90px] text-right">
                  {formatMoney(projTotal, { showZero: true })}
                </span>
              </button>

              <div
                className={`overflow-hidden transition-all duration-200 ${open ? "max-h-none" : "max-h-[110px]"}`}
                style={open ? undefined : { borderTop: "1px solid rgba(220,215,205,0.4)" }}
              >
                <div className="px-2 md:px-4 pb-3">
                  <CategoryTable
                    months={data.months}
                    categories={open || p.project_type === "land" ? (p.categories || data.categories) : data.categories}
                    matrix={p.matrix}
                    rowTotals={p.row_totals}
                    compact
                    singleRowKey={open ? null : "sales_booked"}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------------- Page ---------------- //
function monthsBetween(startIso, endIso) {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const n = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
  return Math.max(1, n);
}

export default function Collections() {
  const [horizon, setHorizon] = useState(18);
  const [customRange, setCustomRange] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [summary, setSummary] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (customRange) {
        params.set("horizon", String(monthsBetween(customRange.start, customRange.end)));
        params.set("as_of", customRange.start);
      } else {
        params.set("horizon", String(horizon));
      }
      const q = params.toString();
      const [s, d] = await Promise.all([
        api.get(`/collections/summary?${q}`),
        api.get(`/dashboard?${q}`),
      ]);
      setSummary(s.data);
      setDashboard(d.data);
    } catch (e) {
      toast.error("Failed to load collections", { description: formatApiError(e.response?.data?.detail) });
    }
  }, [horizon, customRange]);

  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    try {
      const params = new URLSearchParams();
      if (customRange) {
        params.set("horizon", String(monthsBetween(customRange.start, customRange.end)));
        params.set("as_of", customRange.start);
      } else {
        params.set("horizon", String(horizon));
      }
      const { data } = await api.get(`/exports/collections_model.xlsx?${params}`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = customRange
        ? `collections_model_${customRange.start.slice(0,7)}_to_${customRange.end.slice(0,7)}.xlsx`
        : `collections_model_${horizon}m.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error("Export failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const projTotal = dashboard?.kpi_projected_total ?? 0;

  return (
    <PageShell
      testId={TEST_IDS.collectionsPage}
      kicker="Excel-grade CFO View"
      title="Collections Model"
      hideProjectFilter
      rangeMonths={summary?.months}
      rangeLabel={customRange ? `${monthsBetween(customRange.start, customRange.end)}m · custom` : `${horizon}m`}
      actions={
        <div className="cf-hscroll -mx-4 md:mx-0 px-4 md:px-0 w-full md:w-auto">
          <div className="inline-flex items-center gap-2">
            <HorizonSelector value={customRange ? null : horizon} onChange={(h) => { setHorizon(h); setCustomRange(null); }} months={summary?.months} />
            <button
              data-testid="collections-custom-range-btn"
              onClick={() => setShowCustom(true)}
              className={`cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold transition-colors ${customRange ? "bg-ink text-white" : "bg-surface/70 border border-white/70 text-ink"}`}
              title="Pick a custom start / end month"
            >
              <CalendarRange size={13} /> {customRange ? `${customRange.start.slice(0,7)} → ${customRange.end.slice(0,7)}` : "Custom"}
              {customRange && <X size={12} className="ml-1" onClick={(e) => { e.stopPropagation(); setCustomRange(null); }} />}
            </button>
            <button
              data-testid={TEST_IDS.collectionsExportBtn}
              onClick={exportXlsx}
              className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-ink text-white text-sm font-medium hover:bg-ink/90 transition-colors"
            >
              <Download size={13} className="text-gold-500" /> Export
            </button>
          </div>
        </div>
      }
    >
      {/* Summary strip — Projected only. Actuals + variance live on the
          dedicated Actuals page. */}
      {dashboard && (
        <div className="cf-card p-4 md:p-5">
          <div className="flex items-center justify-between gap-3 md:gap-6">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Projected · {horizon}m</div>
              <div className="num text-base md:text-lg text-ink font-bold mt-0.5 truncate">{formatMoney(projTotal)}</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold text-right shrink-0">
              {summary?.projects?.length ?? 0} projects<br />
              <span className="text-muted-light/70">× {summary?.months?.length ?? 0} months</span>
            </div>
          </div>
        </div>
      )}

      {/* Row count / summary counters (filters removed per redesign) */}
      <div className="cf-hscroll -mx-4 md:mx-0 px-4 md:px-0">
        <div className="flex items-center gap-2 min-w-max md:flex-wrap justify-end">
          <span className="text-[10px] md:text-xs text-muted-light num md:ml-auto whitespace-nowrap">
            {`${summary?.projects?.length ?? 0} projects × ${summary?.months?.length ?? 0} months`}
          </span>
        </div>
      </div>

      <SummaryTable data={summary} />
      <ProjectBreakdown data={summary} />

      {showCustom && (
        <CustomRangeModal
          initial={customRange}
          onCancel={() => setShowCustom(false)}
          onApply={(range) => { setCustomRange(range); setShowCustom(false); }}
        />
      )}
    </PageShell>
  );
}

/**
 * Custom start/end month picker. Mirrors the Projection page's picker so
 * both surfaces feel identical when the finance team drills into a specific
 * window (e.g. FY26-27 = Apr-26 to Mar-27).
 */
function CustomRangeModal({ initial, onCancel, onApply }) {
  const FLOOR = "2026-07";
  const _today = new Date();
  const _todayIso = `${_today.getFullYear()}-${String(_today.getMonth() + 1).padStart(2, "0")}`;
  const _defaultStart = _todayIso >= FLOOR ? _todayIso : FLOOR;
  const [start, setStart] = useState((initial?.start || `${_defaultStart}-01`).slice(0, 7));
  const [end, setEnd] = useState((initial?.end || "2027-12-01").slice(0, 7));
  const startErr = start < FLOOR ? "Start must be July 2026 or later" : null;
  const endErr = end <= start ? "End must be after start" : null;
  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div className="glass-card max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg text-ink">Custom timeline</h3>
          <button onClick={onCancel} className="p-1.5 text-muted-dark hover:text-ink"><X size={14} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <label>
            <span className="block text-muted-dark mb-1">Start month</span>
            <input type="month" min={FLOOR} value={start} onChange={(e) => setStart(e.target.value)} className="w-full px-2 py-2 bg-surface rounded-lg num" />
            {startErr && <div className="mt-1 text-red-600">{startErr}</div>}
          </label>
          <label>
            <span className="block text-muted-dark mb-1">End month</span>
            <input type="month" min={FLOOR} value={end} onChange={(e) => setEnd(e.target.value)} className="w-full px-2 py-2 bg-surface rounded-lg num" />
            {endErr && <div className="mt-1 text-red-600">{endErr}</div>}
          </label>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 text-xs text-muted-dark">Cancel</button>
          <button
            data-testid="collections-custom-range-apply"
            disabled={!!startErr || !!endErr}
            onClick={() => onApply({ start: start + "-01", end: end + "-01" })}
            className="px-4 py-2 text-sm rounded-full bg-ink text-white disabled:opacity-50"
          >Apply</button>
        </div>
      </div>
    </div>
  );
}
